/** Application shell — port of `App` (L6279-6357): watermark, grouped sidebar, footer. */
import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { NAV, PAGE_PERMISSIONS, PIROCHI_LOGO, cn, type Permission } from '@pirochi/shared';
import { useAuth } from '@/app/providers/AuthProvider';
import { useSettings } from '@/hooks/useSettings';
import { DORMANT_NAV, EXTRA_NAV, ROUTES } from '@/app/router/routes';
import { t } from '@/i18n/ar';
import { ResetDataModal } from '@/features/settings/ResetDataModal';

export function AppShell() {
  const { user, can, logout } = useAuth();
  const { flags } = useSettings();
  const navigate = useNavigate();
  const [showReset, setShowReset] = useState(false);

  const items = [...NAV, ...EXTRA_NAV, ...(flags.showDormantModules ? DORMANT_NAV : [])].filter((i) =>
    can(PAGE_PERMISSIONS[i.id] as Permission),
  );
  const canSettings = ['settings.manage', 'users.manage', 'accounting.manage', 'migration.run', 'audit.view'].some((p) => can(p as Permission));
  let lastG = '';

  return (
    <div className="min-h-screen relative">
      <a href="#main" className="skip-link">
        {t.skipToContent}
      </a>
      <div className="brand-watermark" aria-hidden="true">
        <img className="wm-img" src={PIROCHI_LOGO} alt="" />
        <div className="wm" style={{ top: '12%', left: '6%' }}>
          PIROCHI
        </div>
        <div className="wm" style={{ bottom: '6%', right: '-2%', fontSize: 'clamp(2.5rem,8vw,5rem)' }}>
          PIROCHI
        </div>
      </div>
      <div className="main-shell mx-auto flex min-h-screen max-w-[1600px] flex-col lg:flex-row">
        <aside
          className="no-print flex w-full flex-col border-t border-[#e0c8ce] lg:w-72 lg:border-l lg:border-t-0 lg:min-h-screen"
          style={{ background: 'linear-gradient(180deg,#FBF9F4,#F6F1E8,#f3e6e9)' }}
        >
          <div className="flex items-center gap-3 px-5 py-6">
            <div className="brand-grad flex h-12 w-12 items-center justify-center rounded-2xl text-xl font-extrabold text-white shadow-lg">P</div>
            <div>
              <div className="text-base font-extrabold text-[#5C1528]">{t.appName}</div>
              <div className="text-xs text-[#9A8A6A]">{t.division}</div>
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 pb-4" aria-label="القائمة الرئيسية">
            {items.map((item) => {
              const showG = item.g !== lastG;
              if (item.g) lastG = item.g;
              return (
                <div key={item.id}>
                  {showG && <div className="mb-1.5 mt-4 px-3 text-[10px] font-bold uppercase tracking-wider text-[#a84d62]">{item.g}</div>}
                  <NavLink
                    to={ROUTES[item.id]!}
                    end={item.id === 'dashboard'}
                    className={({ isActive }) =>
                      cn('mb-1 flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-right text-sm font-semibold', isActive ? 'nav-a' : 'text-[#a84d62] hover:bg-white/70')
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={cn('h-2 w-2 rounded-full shrink-0', isActive ? 'bg-[#8B2942]' : 'bg-[#e0c8ce]')}></span>
                        {item.label}
                      </>
                    )}
                  </NavLink>
                </div>
              );
            })}
            {canSettings && (
              <div>
                <div className="mb-1.5 mt-4 px-3 text-[10px] font-bold uppercase tracking-wider text-[#a84d62]">{t.system}</div>
                <NavLink
                  to={ROUTES.settings!}
                  className={({ isActive }) =>
                    cn('mb-1 flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-right text-sm font-semibold', isActive ? 'nav-a' : 'text-[#a84d62] hover:bg-white/70')
                  }
                >
                  <span className="h-2 w-2 rounded-full shrink-0 bg-[#e0c8ce]"></span>
                  {t.settings}
                </NavLink>
              </div>
            )}
          </nav>
          <div className="border-t border-[#e0c8ce] p-4 space-y-2">
            <div className="rounded-2xl bg-white/80 p-3 shadow-sm">
              <div className="text-sm font-bold text-[#5C2A35]">{t.brandFull}</div>
              <div className="text-[11px] text-[#a84d62] mt-1">{t.credit}</div>
            </div>
            {user && (
              <div className="flex items-center justify-between rounded-xl bg-white/60 px-3 py-2 text-xs text-[#5C2A35]">
                <span className="truncate" title={user.email}>
                  {user.name}
                </span>
                <button
                  type="button"
                  className="font-bold text-[#8B2942]"
                  onClick={async () => {
                    await logout();
                    navigate('/login');
                  }}
                >
                  {t.logout}
                </button>
              </div>
            )}
            {can('data.reset') && (
              <button type="button" onClick={() => setShowReset(true)} className="w-full text-xs text-[#c62828] py-2 rounded-xl hover:bg-rose-50">
                {t.clearAll}
              </button>
            )}
          </div>
        </aside>
        <main id="main" className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8 flex flex-col">
          <div className="flex-1">
            <Outlet />
          </div>
          <div className="mt-8 pt-4 border-t border-[#e8d5d9] text-center text-[11px] text-[#a84d62] no-print">{t.footer}</div>
        </main>
      </div>
      {showReset && <ResetDataModal onClose={() => setShowReset(false)} />}
    </div>
  );
}
